import {
  alpha,
  Box,
  darken,
  Grid,
  lighten,
  ListItemButton,
  Typography,
  useTheme
} from '@mui/material'
import TouchRipple from '@mui/material/ButtonBase/TouchRipple'
import { withObservables } from '@nozbe/watermelondb/react'
import { useTour } from '@reactour/tour'
import Analytics, { Events } from 'analytics/Analytics'
import { AddIcon, ArrowAllCalendars, LayersIcon } from 'assets/icons'
import { SegmentItem } from 'components/buttons/SegmentButton'
import { GroupCardButton, GroupCardItem } from 'components/cards/styled'
import GroupItemContextMenu, {
  GroupMutationEvent
} from 'components/contextMenu/GroupItemContextMenu'
import LabelItemContextMenu from 'components/contextMenu/LabelContextMenu'
import {
  ONBOARDING_MODE,
  useTfmTourProvider
} from 'components/onboarding/TFMTourProvider'
import { SEARCH_MODE } from 'components/popups/searchItems/SearchPopup'
import { AppUIType, useAppUIType } from 'components/providers/AppUITypeProvider'
import { useCalendar } from 'components/providers/CalendarProvider'
import { useCalendarSync } from 'components/providers/CalendarSyncProdivder'
import { useConnection } from 'components/providers/ConnectionProvider'
import { useLandlineCheck } from 'components/providers/LandlineCheckProvider'
import SearchPopupButton from 'components/SearchPopupButton'
import dayjs from 'dayjs'
import { AnimatePresence, Reorder } from 'framer-motion'
import usePersistState from 'hooks/usePersistState'
import { t } from 'i18next'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Database from 'services/db/Database'
import ObserveService from 'services/db/Observe'
import { allCalendarItems } from 'services/db/Queries/Event'
import {
  AddTempGroup,
  allGroupsForUI,
  GroupFilter,
  RemoveTempGroups
} from 'services/db/Queries/GroupQueries'
import { addTempLabel } from 'services/db/Queries/MasterLabel'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import {
  AccountDoc,
  EventDoc,
  EventStatus,
  GroupDoc,
  LastMessage,
  MasterLabelDoc,
  MasterLabelLabel,
  UserSettingsDoc
} from 'types/api/changes'
import { Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import useFitText from 'use-fit-text'
import { buildEventsArrayForUIFromDBEventsWithRecurrentEvents } from 'utils/api/events'
import { is24meApp, isGroupcalApp } from 'utils/appType'
import { APP_COLOR } from 'utils/colors'
import { groupToProvider, isConvertedGroup, ProviderType } from 'utils/groups'
import { isUserOutOfTierBasedOnLandline } from 'utils/tier'

import { GroupsList } from './GroupsList'
import HorizontalChipsBar, { ChipData } from './HorizontalChips'
import LabelsList from './LabelsList'
import * as Styled from './styled'

export interface LastUpdateMessagePerGroup {
  groupId: string
  lastUpdateMessage: LastMessage
}

export interface GroupsProps {
  masterLabel?: Model<MasterLabelDoc>
  groups?: Model<GroupDoc>[] | GroupDoc[]
  topGroups?: Model<GroupDoc>[]
  userSettings?: Model<UserSettingsDoc> | UserSettingsDoc
  account?: Model<AccountDoc>
}

export interface GroupContextMenuProps {
  x: number
  y: number
  group: GroupDoc
  containerTop: number
}

interface LabelContextMenuProps {
  x: number
  y: number
  label: MasterLabelLabel
}

function Groups(props: GroupsProps) {
  'use memo'

  const theme = useTheme()
  const landlineCheck = useLandlineCheck()
  const uiTypeContext = useAppUIType()
  const tour = useTour()
  const tfmTour = useTfmTourProvider()
  const calendar = useCalendar()
  const connection = useConnection()
  const [lastUpdateMessages, setLastUpdateMessages] = useState<
    LastUpdateMessagePerGroup[]
  >([])

  const [pendingGroup, setPendingGroup] = useState<
    Model<GroupDoc> | undefined
  >()
  const [addGroupPopupState, onAddGroupPopupState] = useState(false)
  const [groupFilter, setGroupFilter] = usePersistState(
    GroupFilter.LastUpdated.valueOf(),
    LocalStorageKey.GROUP_FILTER
  )
  const [archivedOverrides, setArchivedOverrides] = useState<
    Record<string, boolean>
  >({})
  const topRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const onCancelAdd = useCallback(() => {
    RemoveTempGroups()
    onAddGroupPopupState(false)
    LocalStorage.remove(LocalStorageKey.ADDING_GROUP)
    setPendingGroup(undefined)
  }, [])

  const handleAddGroup = useCallback(() => {
    if (isUserOutOfTierBasedOnLandline(props.account)) {
      landlineCheck.onShowOutOfTierBasedOnLandline(true)
      return
    }
    if (connection.isConnectionAvailable()) {
      if (is24meApp()) {
        //add label here
        addTempLabel()
      } else {
        tour.setIsOpen(false)
        if (addGroupPopupState) return
        ;(listRef.current as HTMLDivElement | null)?.scrollTo(0, 0)
        Analytics.getInstance().sendEvent(Events.ADD_GROUP)
        addTempGroupToDB().then((group) => {
          console.log('added group to db', group)
          onAddGroupPopupState(true)
          setPendingGroup(group)
        })
      }
    }
  }, [
    props.account,
    landlineCheck,
    connection,
    addGroupPopupState,
    tour,
    listRef
  ])

  const showAllCalendars = useCallback(() => {
    uiTypeContext.setSelectedLabel(undefined)
    calendar.onGroupServer('')
    LocalStorage.remove(LocalStorageKey.PENDING_EVENT_DATA)
    onCancelAdd()

    if (tour.setSteps && tfmTour.getStepsByMode) {
      const steps = tfmTour.getStepsByMode(ONBOARDING_MODE.FIRST_LOGIN)
      if (tfmTour.isPlaying) return
      tfmTour.setMode(ONBOARDING_MODE.FIRST_LOGIN)
      tour.setSteps(steps)
      tour.setCurrentStep(0)
      if (steps.length > 0) {
        tour.setIsOpen(true)
      }
    }
  }, [uiTypeContext, calendar, onCancelAdd, tour, tfmTour])

  const { t, i18n } = useTranslation()

  let groupsForUI: GroupDoc[] = allGroupsForUI(
    props.groups ?? [],
    props.userSettings,
    groupFilter === GroupFilter.Archived,
    groupFilter
  ).sort((a, b) => {
    const providerA = groupToProvider(a)
    const providerB = groupToProvider(b)

    return b._sortOrder - a._sortOrder
  })

  groupsForUI = groupsForUI.filter((group) => {
    const groupId = group._id ?? ''
    const overrideArchived = archivedOverrides[groupId]

    if (overrideArchived === undefined) {
      return true
    }

    return groupFilter === GroupFilter.Archived
      ? overrideArchived
      : !overrideArchived
  })

  useEffect(() => {
    if (groupFilter === GroupFilter.NextEvent) {
      allCalendarItems().then((events) => {
        const allEvents = buildEventsArrayForUIFromDBEventsWithRecurrentEvents(
          events,
          dayjs().subtract(7, 'day'),
          dayjs().add(7, 'day')
        )

        const lastMessages: LastUpdateMessagePerGroup[] = []
        groupsForUI.forEach((group) => {
          // find closest event to now in the group
          let closestEvent: EventDoc | undefined = undefined
          let closestEventTime: number = Number.MAX_SAFE_INTEGER
          for (let i = 0; i < allEvents.length; i++) {
            const event = allEvents[i]
            if (event.GroupID === group._id) {
              const eventTime = Number(event.StartDate)
              if (
                eventTime < closestEventTime &&
                eventTime > dayjs().unix() &&
                event.Status === EventStatus.ACTIVE
              ) {
                closestEvent = event
                closestEventTime = eventTime
              }
            }
          }

          if (closestEvent) {
            const LastUpdateMessagePerGroup: LastUpdateMessagePerGroup = {
              groupId: group._id ?? '',
              lastUpdateMessage: {
                type: closestEvent.Text ?? '',
                when: dayjs(Number(closestEvent.StartDate) * 1000).format('lll')
              }
            }
            lastMessages.push(LastUpdateMessagePerGroup)
          } else {
            const LastUpdateMessagePerGroup: LastUpdateMessagePerGroup = {
              groupId: group._id ?? '',
              lastUpdateMessage: {
                type: t('noEventsComingUp')
              }
            }
            lastMessages.push(LastUpdateMessagePerGroup)
          }
        })

        // resort groups by closest event
        groupsForUI = groupsForUI.sort((a, b) => {
          const groupA = lastMessages.find((m) => m.groupId === a._id)
          const groupB = lastMessages.find((m) => m.groupId === b._id)
          if (groupA && groupB) {
            return (
              dayjs(groupB.lastUpdateMessage.when).unix() -
              dayjs(groupA.lastUpdateMessage.when).unix()
            )
          }
          return 0
        })

        setLastUpdateMessages(lastMessages)
      })
    } else {
      setLastUpdateMessages([])
    }
  }, [groupFilter])

  const [contextMenuPosition, setContextMenuPosition] =
    useState<GroupContextMenuProps | null>(null)
  const [contextMenuPositionLabel, setContextMenuPositionLabel] =
    useState<LabelContextMenuProps | null>(null)
  const [groupsMutationTick, setGroupsMutationTick] = useState(0)

  const containerRef = useRef<HTMLDivElement>(null)

  const handleCloseContextMenu = useCallback(() => {
    setContextMenuPosition(null)
    setContextMenuPositionLabel(null)
    LocalStorage.remove(LocalStorageKey.CONTEXT_FOR_GROUP)
  }, [])

  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>, group: GroupDoc) => {
      event.preventDefault() // Prevent the default context menu from showing
      handleCloseContextMenu() // Close the existing context menu

      const containerRect = containerRef?.current?.getBoundingClientRect()
      if (!containerRect) return

      const offsetX =
        event.clientX -
        (i18n.dir() === 'rtl' ? containerRect.right : containerRect.left)
      const offsetY = event.clientY - containerRect.top

      setContextMenuPosition({
        x: offsetX,
        y: offsetY,
        group,
        containerTop: containerRect.top
      })
    },
    [handleCloseContextMenu, i18n]
  )

  const handleLabelContextMenu = useCallback(
    (event: React.MouseEvent<HTMLLIElement>, label: MasterLabelLabel) => {
      event.preventDefault() // Prevent the default context menu from showing
      handleCloseContextMenu() // Close the existing context menu

      const containerRect = containerRef.current?.getBoundingClientRect()
      if (!containerRect) return

      const offsetX =
        event.clientX -
        (i18n.dir() === 'rtl' ? containerRect.right : containerRect.left)
      const offsetY = event.clientY - containerRect.top

      setContextMenuPositionLabel({ x: offsetX, y: offsetY, label })
    },
    [handleCloseContextMenu, i18n]
  )

  const calendarSync = useCalendarSync()
  return (
    <Styled.Groups id="first-group">
      <Grid
        gap={'0.5rem'}
        display={'flex'}
        flexDirection={'row'}
        alignItems={'center'}
        width={'100%'}
      >
        <SearchPopupButton
          spanId="global-search-button"
          border
          textfieldPlaceholder={`${
            is24meApp()
              ? t('searchItems', { what: t('eventsTasksNotes').toLowerCase() })
              : t('searchEvents')
          }`}
          mode={is24meApp() ? SEARCH_MODE.OnlyAllEvents : SEARCH_MODE.All}
        />
        <GroupCardItem
          style={{
            border:
              theme.palette.mode === 'dark'
                ? 'none'
                : `1px solid rgba(177, 186, 200, ${
                    !calendar.groupServerID && !uiTypeContext.selectedLabel
                      ? 1
                      : 0.25
                  })`,
            boxShadow:
              theme.palette.mode === 'dark'
                ? 'none'
                : '0px 4px 10px rgba(177, 186, 200, 0.2)',
            borderRadius: '24px'
          }}
        >
          <GroupCardButton
            id="all-calendars-button"
            selected={!calendar.groupServerID && !uiTypeContext.selectedLabel}
            sx={{
              borderRadius: '24px',
              backgroundColor: theme.palette.surface.hover,
              border: '1px solid transparent',
              '&.Mui-selected': {
                backgroundColor: `${theme.palette.mode === 'dark' ? alpha(APP_COLOR, 0.4) : lighten(APP_COLOR, 0.8)} !important` // Change this to your desired selected color
              }
            }}
            onClick={() => {
              showAllCalendars()
            }}
          >
            <Grid item alignItems={'center'} display={'flex'}>
              <LayersIcon />
            </Grid>
            <AllCalendarsDiv />
            <ArrowAllCalendars
              style={{
                transform: i18n.dir() === 'ltr' ? '' : 'rotate(180deg)'
              }}
            />
          </GroupCardButton>
        </GroupCardItem>
      </Grid>
      {isGroupcalApp() && (
        <HorizontalChipsBar
          chips={[
            {
              title: t('lastUpdated'),
              key: GroupFilter.LastUpdated
            },
            {
              title: t('nextEvent'),
              key: GroupFilter.NextEvent
            },
            {
              title: t('unread'),
              key: GroupFilter.Unread
            },
            {
              title: t('archived'),
              key: GroupFilter.Archived
            }
          ]}
          selectedKey={groupFilter}
          onChipSelect={function (selectedChip: ChipData): void {
            setGroupFilter(selectedChip.key)
          }}
        />
      )}
      <Styled.GroupList
        ref={containerRef}
        data-refresh-tick={groupsMutationTick}
        sx={{
          marginTop: is24meApp() ? '1rem' : 0,
          '&& .Mui-selected': {
            bgcolor: theme.palette.button.background.selected
          }
        }}
      >
        <Box>
          {groupsForUI.length === 0 &&
          groupFilter !== GroupFilter.LastUpdated ? (
            <Typography marginTop={'20px'} textAlign={'center'}>
              {t('nothingToShowHere')}
            </Typography>
          ) : (
            <Styled.GroupListScrollContainer
              sx={{
                marginRight: i18n.dir() === 'ltr' ? 0 : theme.spacing(-5),
                marginLeft: i18n.dir() === 'ltr' ? theme.spacing(-5) : 0
              }}
            >
              <Styled.GroupListScroll ref={listRef} id="groups-list-scroll">
                <AnimatePresence>
                  <Reorder.Group
                    axis="y"
                    as="div"
                    values={groupsForUI.map((row) => row.id)}
                    onReorder={() => {}}
                  >
                    {(isGroupcalApp() ||
                      uiTypeContext.uiType.value === AppUIType.calendars) && (
                      <GroupsList
                        account={props.account!}
                        userSettings={props.userSettings}
                        groupsForUI={groupsForUI ?? []}
                        pendingGroup={pendingGroup}
                        addGroupPopupState={addGroupPopupState}
                        onAddGroupPopupState={onAddGroupPopupState}
                        onCancelAdd={onCancelAdd}
                        handleCloseContextMenu={handleCloseContextMenu}
                        handleContextMenu={handleContextMenu}
                        topRef={topRef}
                        lastUpdateMessages={lastUpdateMessages}
                      />
                    )}
                    {is24meApp() &&
                      uiTypeContext.uiType.value != AppUIType.calendars && (
                        <LabelsList
                          masterLabel={props.masterLabel}
                          handleCloseContextMenu={handleCloseContextMenu}
                          handleContextMenu={handleLabelContextMenu}
                        />
                      )}
                  </Reorder.Group>
                </AnimatePresence>
                <Box height={60} />
              </Styled.GroupListScroll>
            </Styled.GroupListScrollContainer>
          )}
          {(isGroupcalApp() ||
            uiTypeContext.uiType.value != AppUIType.calendars) && (
            <Styled.AddGroupButton
              id="add-group-button"
              onClick={handleAddGroup}
            >
              <AddIcon />
              <span>
                {t(
                  uiTypeContext.uiType.value === AppUIType.calendars
                    ? 'calendarButton'
                    : 'label'
                )}
              </span>
            </Styled.AddGroupButton>
          )}
          {is24meApp() &&
            uiTypeContext.uiType.value === AppUIType.calendars && (
              <Styled.AddGroupButton
                id="add-group-button"
                onClick={() => {
                  if (calendarSync.showProviderPicker) {
                    calendarSync.showProviderPicker()
                  }
                }}
              >
                <AddIcon />
                <span>{t('calendarButton')}</span>
              </Styled.AddGroupButton>
            )}
        </Box>
        {contextMenuPosition && (
          <GroupItemContextMenu
            account={props.account}
            userSettings={props.userSettings}
            x={contextMenuPosition?.x ?? 0}
            y={contextMenuPosition?.y ?? 0}
            group={contextMenuPosition.group}
            onClose={handleCloseContextMenu}
            onMutation={(event: GroupMutationEvent) => {
              setGroupsMutationTick((prev) => prev + 1)

              if (event.type === 'archive-toggle') {
                setArchivedOverrides((prev) => ({
                  ...prev,
                  [event.groupId]: event.isArchived === true
                }))
              }

              if (event.type === 'group-removed') {
                setArchivedOverrides((prev) => {
                  if (!prev[event.groupId]) {
                    return prev
                  }

                  const next = { ...prev }
                  delete next[event.groupId]
                  return next
                })
              }
            }}
            contextMenuPosition={contextMenuPosition}
            containerTop={contextMenuPosition.containerTop}
          />
        )}
        {contextMenuPositionLabel && (
          <LabelItemContextMenu
            userSettings={props.userSettings}
            x={contextMenuPositionLabel?.x ?? 0}
            y={contextMenuPositionLabel?.y ?? 0}
            label={contextMenuPositionLabel.label}
            onClose={handleCloseContextMenu}
            masterLabel={props.masterLabel}
          />
        )}
      </Styled.GroupList>
    </Styled.Groups>
  )
}

function AllCalendarsDiv() {
  const uiTypeContext = useAppUIType()
  const theme = useTheme()
  const { fontSize, ref } = useFitText({
    minFontSize: 10,
    maxFontSize: 120
  })

  function allItemsButtonTitle(uiType: SegmentItem): string {
    switch (uiType.value) {
      case AppUIType.calendars:
        return t('allCalendarsButton')
      case AppUIType.notes:
        return t('allNotes')
      case AppUIType.tasks:
        return t('allTasks')
    }

    return ''
  }

  return (
    <div
      ref={ref}
      style={{
        marginLeft: '8px',
        marginRight: '8px',
        color: theme.palette.text.primary,
        fontSize: fontSize,
        textAlign: 'center',
        whiteSpace: 'nowrap',
        flex: '1',
        maxWidth: '160px',
        width: '160px !important'
      }}
    >
      {allItemsButtonTitle(uiTypeContext.uiType)}
    </div>
  )
}

export default withObservables([], () => ({
  masterLabel: ObserveService.MasterLabel(),
  groups: ObserveService.Groups(),
  account: ObserveService.Account(),
  topGroups: ObserveService.TopGroups(),
  userSettings: ObserveService.UserSettings()
}))(Groups)

function addTempGroupToDB() {
  return Database.write(async () => {
    return AddTempGroup()
  })
}
